import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import "./passenger-card.scss";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { Checkbox, ConfigProvider, Select, Space, Button, DatePicker } from "antd";
import {
  phoneCountryCodes,
  iso2ToDialCode,
  getPhoneCodeByIso2,
  countryDialLabel,
} from "../../../constants/phoneCountryCodes";
import dayjs from "dayjs";

const extractISO2 = (c) =>
  (c?.iso2 || c?.iso2_cc || c?.alpha2 || c?.code2 || c?.cca2 || c?.code || "")
    .toString()
    .toUpperCase();

const countryCodeToEmoji = (iso2) => {
  if (!iso2 || iso2.length !== 2) return "🏳️";
  const codePoints = [...iso2.toUpperCase()].map(
    (c) => 127397 + c.charCodeAt(0)
  );
  return String.fromCodePoint(...codePoints);
};

export default function PassengerCard({
  passenger,
  paxIndex,
  onSubmit,
  editable,
  removePassenger,
  deletePassenger,
  onSelect,
  selectedPassengers,
}) {
  const [isEditing, setIsEditing] = useState(editable);

  const guestFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: passenger?.id || "",
      firstName: passenger?.firstName || "",
      lastName: passenger?.lastName || "",
      gender: passenger?.gender || "",
      nationality: passenger?.nationality || "",
      dob: passenger?.dob || "",
      documentType: passenger?.documentType || "Passport",
      documentNumber: passenger?.documentNumber || "",
      expirationDate: passenger?.expirationDate || "",
      noExpirationDate: passenger?.noExpirationDate || false,
    },
    validationSchema: Yup.object({
      firstName: Yup.string()
        .matches(/^[A-Za-z\s]+$/, "Only English letters allowed")
        .required("First name is required"),
      lastName: Yup.string()
        .matches(/^[A-Za-z\s]+$/, "Only English letters allowed")
        .required("Last name is required"),
      gender: Yup.string()
        .oneOf(["Male", "Female"], "Please select gender")
        .required("Gender is required"),
      nationality: Yup.string().required("Nationality is required"),
      dob: Yup.date()
        .required("Date of birth is required")
        .max(new Date(), "Date of birth cannot be in the future")
        .typeError("Invalid date format (YYYY-MM-DD)"),
    }),
    onSubmit: (values) => {
      onSubmit?.(values);
      setIsEditing(false);
    },
  });

  if (!isEditing && passenger) {
    // View-card
    return (
      <div className="passenger-card view-card">
        <div className="passenger-card-left">
          <ConfigProvider
            theme={{
              token: {
                colorPrimary: "#CBA135",
                colorPrimaryBorder: "#CBA135",
                colorPrimaryHover: "#CBA135",
              },
            }}
          >
            <Checkbox onChange={onSelect} checked={!!selectedPassengers[paxIndex]} ></Checkbox>
          </ConfigProvider>
        </div>
        <div className="passenger-card-right">
          <div className="card-header">
            <div className="passenger-name">
              {passenger.firstName} {passenger.lastName}
              {passenger.type !== "Adult" && (
                <span className="passenger-type">{passenger.type}</span>
              )}
            </div>
            <div>
              <button
                className="edit-btn"
                style={{ marginRight: "12px" }}
                onClick={() => setIsEditing(true)}
              >
                <EditOutlined />
              </button>
              <button className="edit-btn" onClick={deletePassenger}>
                <DeleteOutlined />
              </button>
            </div>
          </div>
          <div className="card-body">
            <div className="passenger-info-row">
              <span className="info-label">{`${passenger.gender}/${passenger.nationality}`}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form
      className="passenger-card edit-card container"
      onSubmit={guestFormik.handleSubmit}
    >
      <div className="card-header">
        <h5>Edit Passenger</h5>
      </div>

      <div className="card-body row">
        <div className="guest-inputs">
          <div className="col-md-12 p-2">
            <div className="floating-label">
              <input
                type="text"
                name="firstName"
                placeholder="Given/First Name"
                value={guestFormik.values.firstName}
                onChange={guestFormik.handleChange}
                onBlur={guestFormik.handleBlur}
              />
              <label className="floating-label-text">First Name</label>
              {guestFormik.touched.firstName &&
                guestFormik.errors.firstName && (
                  <div className="error">{guestFormik.errors.firstName}</div>
                )}
            </div>
          </div>

          {/* Last Name */}
          <div className="col-md-12 p-2">
            <div className="floating-label">
              <input
                type="text"
                name="lastName"
                placeholder="Last Name"
                value={guestFormik.values.lastName}
                onChange={guestFormik.handleChange}
                onBlur={guestFormik.handleBlur}
              />
              <label className="floating-label-text">Last Name</label>
              {guestFormik.touched.lastName && guestFormik.errors.lastName && (
                <div className="error">{guestFormik.errors.lastName}</div>
              )}
            </div>
          </div>

          {/* Date of Birth */}
          <div className="col-md-12 p-2">
            <div className="floating-label">
              {/* <input
                type="text"
                name="dob"
                placeholder="YYYY-MM-DD"
                value={guestFormik.values.dob}
                onChange={guestFormik.handleChange}
                onBlur={guestFormik.handleBlur}
              />
              <label className="floating-label-text">Date of Birth</label>
              {guestFormik.touched.dob && guestFormik.errors.dob && (
                <div className="error">{guestFormik.errors.dob}</div>
              )} */}
              <div className="label-text">Date of Birth</div>
            </div>
            <ConfigProvider
                theme={{
                  components: {
                    DatePicker: {
                      activeBorderColor: "#CBA135",
                      cellRangeBorderColor: "#CBA135",
                      hoverBorderColor: "#CBA135",
                      cellHoverWithRangeBg: "rgba(203,161,53,0.2)",
                      controlHeight: 40,
                    },
                  },
                  token: {
                    colorPrimary: "#CBA135",
                    colorPrimaryBorder: "#CBA135",
                  },
                }}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="YYYY-MM-DD"
                  value={guestFormik.values.dob ? dayjs(guestFormik.values.dob) : null}
                  onChange={(date, dateString) =>
                    guestFormik.setFieldValue("dob", dateString)
                  }
                />
              </ConfigProvider>
              {guestFormik.touched.dob &&
                guestFormik.errors.dob && (
                  <div className="error">{guestFormik.errors.dob}</div>
                )}
          </div>

          {/* Nationality */}
          <div className="col-md-12 p-2">
            <div className="floating-label">
              <div className="label-text">Nationality</div>
            </div>
            <ConfigProvider
                theme={{
                  components: {
                    Select: {
                      activeBorderColor: '#CBA135',
                      hoverBorderColor: '#CBA135',
                    },
                  },
                  token: {
                    controlHeight: 40
                  }
                }}
              >
                <Select
                  className="nationality-select"
                  showSearch
                  value={guestFormik.values.nationality}
                  onChange={(value) => guestFormik.setFieldValue("nationality", value)}
                  options={(phoneCountryCodes ?? []).map((c) => {
                    const iso2 = extractISO2(c);
                    return {
                      value: c.name,
                      label: c.name,
                      emoji: countryCodeToEmoji(iso2),
                    };
                  })}
                  optionRender={(option) => (
                    <Space>
                      <span role="img" aria-label={option.data.label}>
                        {option.data.emoji}
                      </span>
                      {option.data.label}
                    </Space>
                  )}
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </ConfigProvider>
              {guestFormik.touched.nationality &&
                guestFormik.errors.nationality && (
                  <div className="error">{guestFormik.errors.nationality}</div>
                )}
          </div>

          {/* Gender */}
          <div className="col-md-12 p-2">
            <div className="floating-label gender-box">
              <label className="floating-label-text2">
                Gender on ID <span className="text-danger">*</span>
              </label>

              <div className="checks-wrap">
                <label className="custom-radio">
                  <input
                    type="radio"
                    name="gender"
                    value="Male"
                    checked={guestFormik.values.gender === "Male"}
                    onChange={guestFormik.handleChange}
                  />
                  <span className="radio-mark"></span> Male
                </label>

                <label className="custom-radio">
                  <input
                    type="radio"
                    name="gender"
                    value="Female"
                    checked={guestFormik.values.gender === "Female"}
                    onChange={guestFormik.handleChange}
                  />
                  <span className="radio-mark"></span> Female
                </label>
              </div>

              {guestFormik.touched.gender && guestFormik.errors.gender && (
                <div className="error">{guestFormik.errors.gender}</div>
              )}
            </div>
          </div>

          {/* Document Number */}
          {/* <div className="col-md-6 p-2">
            <div className="floating-label">
              <input
                type="text"
                name="documentNumber"
                placeholder="Document Number"
                value={guestFormik.values.documentNumber}
                onChange={guestFormik.handleChange}
              />
              <label className="floating-label-text">
                {guestFormik.values.documentType || "Document"} Number
              </label>
            </div>
          </div> */}

          {/* Expiration Date */}
          {/* <div className="col-md-6 p-2">
            <div className="floating-label">
              <input
                type="date"
                name="expirationDate"
                value={guestFormik.values.expirationDate}
                onChange={guestFormik.handleChange}
              />
              <label className="floating-label-text">Expiration Date</label>
            </div>
          </div> */}
        </div>

        {/* Buttons */}
        <div className="d-flex">
          <div
            className="col-12 p-2 d-flex gap-2"
            style={{ justifyContent: "flex-end" }}
          >
            <Button
              className="close-btn"
              onClick={() => {
                if (!passenger) {
                  removePassenger?.(paxIndex);
                } else {
                  setIsEditing(false);
                }
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="save-btn"
              onClick={guestFormik.submitForm}
              loading={guestFormik.isSubmitting}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
}
